/**
 * useSubscriptionStatus Hook
 *
 * Fetches comprehensive subscription status including payment health,
 * usage metrics, and trial information. Used by Dashboard and Billing pages.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import type { SubscriptionLimits } from './useSubscriptionTiers';

export interface SubscriptionStatusData {
  subscription: {
    id: string;
    tier: string;
    status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    trialEnd: string | null;
  };
  plan: {
    name: string;
    priceMonthly: number;
    priceYearly: number;
    limits: SubscriptionLimits;
    features: string[];
  };
  usage: {
    repositoriesAnalyzed: number;
    apiRequestsCount: number;
    storageUsedMB: number;
    teamMembersCount: number;
    month: string;
  };
  usagePercentages: {
    repositories: number;
    teamMembers: number;
    storage: number;
  };
  usageWarnings: {
    repositories: boolean;
    teamMembers: boolean;
    storage: boolean;
  };
  paymentIssue: boolean;
  requiresAction: boolean;
  isTrial: boolean;
  trialDaysRemaining: number | null;
}

export function useSubscriptionStatus() {
  const api = useAuthenticatedApi();

  return useQuery({
    queryKey: ['subscription-status'],
    queryFn: async () => {
      const response = await api.get<SubscriptionStatusData>('/subscriptions/status');
      return response.data;
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
}
